/* eslint-disable import/prefer-default-export */

// @ts-check

import { AUDITOR_PRIVILEGES } from 'src/domains/noteAuditor/domain';
import { userRoles } from 'src/utils/AppUtils';

export const PRIVILEGES =/** @type {const} */ ({
  AUDITOR: AUDITOR_PRIVILEGES,
  CLIENTS: {
    ACCESS_ALL_CLIENTS_IN_OWN_UNITS: 'clients/accessAllClientsInOwnUnits',
    ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS: 'clients/accessAllStaffMembersInOwnUnits',
    ADD_CLIENTS: 'clients/addClients',
  },
  DISABLE_REQUIRED_ACTIONS: {
    PENDING_ACTIVITIES: 'disableRequiredActions/pendingActivities',
    PENDING_NOTES: 'disableRequiredActions/pendingNotes',
    PENDING_REPORT_REVIEW: 'disableRequiredActions/pendingReportReview',
    PENDING_SESSION: 'disableRequiredActions/pendingSession',
    PENDING_SIGNATURES: 'disableRequiredActions/pendingSignatures',
  },
  FILES_AND_FORMS: {
    DELETE_FILES: 'filesAndForms/deleteFiles',
    DELETE_FORMS: 'filesAndForms/deleteForms',
    MANAGE_FILES: 'filesAndForms/manageFiles',
    MANAGE_FOLDERS: 'filesAndForms/manageFolders',
    MANAGE_FORMS: 'filesAndForms/manageForms',
  },
  HOME: {
    SHOW_ALL_CLIENT_PROGRAMS: 'home/showAllClientPrograms',
  },
  LIBRARY: {
    ACCESS_TEMPLATES_FOLDER: 'library/accessTemplatesFolder',
    ADD_FOLDERS: 'library/addFolders',
    ADD_PROGRAMS: 'library/addPrograms',
    BULK_ADD_TAGS: 'library/bulkAddTags',
    BULK_DELETE_PROGRAMS: 'library/bulkDeletePrograms',
    DELETE_FOLDERS: 'library/deleteFolders',
    DELETE_PROGRAMS: 'library/deletePrograms',
    EDIT_PROGRAMS: 'library/editPrograms',
    MOVE_FOLDERS: 'library/moveFolders',
    MOVE_PROGRAMS: 'library/movePrograms',
    RENAME_FOLDERS: 'library/renameFolders',
    SHARE_FOLDERS: 'library/shareFolders',
  },
  NOTIFICATIONS: {
    CANCELLED_APPOINTMENT: 'notifications/cancelledAppointment',
    COMMENTS: 'notifications/comments',
    FILE_UPLOAD_FAILED: 'notifications/fileUploadFailed',
    MISSED_APPOINTMENT: 'notifications/missedAppointment',
    PENDING_ACTIVITIES: 'notifications/pendingActivities',
    PENDING_NOTES: 'notifications/pendingNotes',
    PENDING_REPORT_REVIEW: 'notifications/pendingReportReview',
    PENDING_SESSION: 'notifications/pendingSession',
    PENDING_SIGNATURES: 'notifications/pendingSignatures',
    PROGRAM_MODIFICATION: 'notifications/programModification',
    RESTORED_APPOINTMENT: 'notifications/restoredAppointment',
    SESSION_FILES_ATTACHED: 'notifications/sessionFilesAttached',
    SESSION_FINISHED: 'notifications/sessionFinished',
    SESSION_REMINDER: 'notifications/sessionReminder',
    TARGET_MASTERY: 'notifications/targetMastery',
    TARGET_REOPENED: 'notifications/targetReopened',
    VIDEO_SESSION_INVITATION: 'notifications/videoSessionInvitation',
  },
  OFFLINE: {
    HAS_ACCESS: 'offline/hasAccess',
  },
  PAGES: {
    BILLING: 'pages/billing',
    CANCELLATION_REASONS: 'pages/cancellationReasons',
    CLIENT_APPOINTMENTS: 'pages/clientAppointments',
    CLIENT_DATA: 'pages/clientData',
    CLIENTS: 'pages/clients',
    FILES: 'pages/files',
    FORMS: 'pages/forms',
    FUNDING_SOURCES: 'pages/fundingSources',
    GLOBAL_AVAILABILITY: 'pages/globalAvailability',
    HISTORY: 'pages/history',
    INTEGRATIONS: 'pages/integrations',
    LIBRARY: 'pages/library',
    MY_SCHEDULE: 'pages/mySchedule',
    NOTES: 'pages/notes',
    PENDING_SIGNATURES: 'pages/pendingSignatures',
    PLACE_OF_SERVICE: 'pages/placeOfService',
    PROGRAM_DATA: 'pages/programData',
    PROGRAM_LOGIC: 'pages/programLogic',
    PROGRAM_TEMPLATES: 'pages/programTemplates',
    PROGRAMS: 'pages/programs',
    PROGRESS: 'pages/progress',
    PROMPT_LEVELS: 'pages/promptLevels',
    PROTOTYPE: 'pages/prototype',
    REPORTING: 'pages/reporting',
    REPORTS: 'pages/reports',
    SCHEDULING: 'pages/scheduling',
    STAFF_INFORMATION: 'pages/staffInformation',
    SUPERVISION_MODE: 'pages/supervisionMode',
    TAGS: 'pages/tags',
    TREATMENT_AREAS: 'pages/treatmentAreas',
    TREATMENT_SERVICES: 'pages/treatmentServices',
    WORKFLOWS: 'pages/workflows',
  },
  PROGRAMS: {
    ADD_PROGRAMS_TO_LIBRARY: 'programs/addProgramsToLibrary',
    ADD_TARGET: 'programs/addTarget',
    CHANGE_PROGRAM_TYPE: 'programs/changeProgramType',
    UPSERT_PROGRAMS_WITH_MIXED_MEASUREMENT_TYPES: 'programs/upsertProgramsWithMixedMeasurementTypes',
    CUSTOMIZE_PROMPT_LEVELS: 'programs/customizePromptLevels',
    EDIT_TARGET_STATUS: 'programs/editTargetStatus',
  },
  REPORTS: {
    ADD_ORG_INSIGHTS_GRAPH: 'reports/addOrgInsightsGraph',
    COMMENT_REPORTS: 'reports/commentReports',
    DELETE_ASSESSMENTS: 'reports/deleteAssessments',
    DELETE_REPORTS: 'reports/deleteReports',
    EDIT_ASSESSMENTS: 'reports/editAssessments',
    EDIT_REPORTS: 'reports/editReports',
    VIEW_ARCHIVED: 'reports/viewArchived',
    VIEW_ASSESSMENTS: 'reports/viewAssessments',
    VIEW_DRAFT: 'reports/viewDraft',
    VIEW_IN_REVIEW: 'reports/viewInReview',
    VIEW_OVERVIEW: 'reports/viewOverview',
  },
  SESSION_ACTIVITIES: {
    ALLOW_APPROVE_CHANGE_REQUESTS: 'sessionActivities/allowApproveChangeRequests',
    ALLOW_OVERLAPPING_ACTIVITIES: 'sessionActivities/allowOverlappingActivities',
    ALLOW_SIGN_AND_SEND: 'sessionActivities/allowSignAndSend',
    ALLOW_VOICE_INPUT: 'sessionActivities/allowVoiceInput',
    CANCEL_SIGNATURES: 'sessionActivities/cancelSignatures',
    COMPLETE_OTHER_USERS_NOTES: 'sessionActivities/completeOtherUsersNotes',
    DELETE: 'sessionActivities/delete',
    DELETE_SIGNATURES: 'sessionActivities/deleteSignatures',
    EDIT_ASSOCIATE_ACTIVITY_TIME_FRAME: 'sessionActivities/editAssociateActivityTimeFrame',
    EDIT_OWN_ACTIVITY_TIME_FRAME: 'sessionActivities/editOwnActivityTimeFrame',
    PRESERVE_SIGNATURES: 'sessionActivities/preserveSignatures',
    REOPEN: 'sessionActivities/reopen',
    SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENT: 'sessionActivities/sendStaffActivitiesWithoutLinkedAppointment',
    UNRENDER: 'sessionActivities/unrender',
  },
  SESSIONS: {
    ADD_COMMENT: 'sessions/addComment',
    AI_AUDIT_AGENT: 'sessions/aiAuditAgent',
    CHANGE_SESSION_NOTE_TEMPLATE: 'sessions/changeSessionNoteTemplate',
    DELETE: 'sessions/delete',
    DELETE_SCHEDULED_SESSIONS: 'sessions/deleteScheduledSessions',
    EDIT_DATA: 'sessions/editData',
    EDIT_SCHEDULED_SESSIONS: 'sessions/editScheduledSessions',
    ENTER_MANUAL_SESSIONS: 'sessions/enterManualSessions',
    LEAVE_OPEN: 'sessions/leaveOpen',
    SIGN_OTHER_SIGNATURE_REQUESTS: 'sessions/signOtherSignatureRequests',
    START_SESSIONS: 'sessions/startSessions',
    START_WITH_PENDING_ACTIVITIES_NOTIFICATION: 'sessions/startWithPendingActivitiesNotification',
    START_WITH_PENDING_NOTES_NOTIFICATION: 'sessions/startWithPendingNotesNotification',
    START_WITH_PENDING_REPORT_REVIEW_NOTIFICATION: 'sessions/startWithPendingReportReviewNotification',
    START_WITH_PENDING_SESSION_NOTIFICATION: 'sessions/startWithPendingSessionNotification',
    START_WITH_PENDING_SIGNATURES_NOTIFICATION: 'sessions/startWithPendingSignaturesNotification',
    SUPERVISE_SESSIONS: 'sessions/superviseSessions',
  },
  USERS: {
    ADD_STAFF_PROFILES: 'users/addStaffProfiles',
    MANAGE_USERS: 'users/manageUsers',
  },
});

/** @typedef {typeof PRIVILEGES} Privileges */
/** @typedef {{ [KEY in keyof Privileges]: Privileges[KEY][keyof Privileges[KEY]] }[keyof Privileges]} Privilege */

const DEFAULT_PRIVILEGES = /** @type {const} */ ({
  DISABLE_REQUIRED_ACTIONS: {
    [PRIVILEGES.DISABLE_REQUIRED_ACTIONS.PENDING_ACTIVITIES]: false,
    [PRIVILEGES.DISABLE_REQUIRED_ACTIONS.PENDING_NOTES]: false,
    [PRIVILEGES.DISABLE_REQUIRED_ACTIONS.PENDING_REPORT_REVIEW]: false,
    [PRIVILEGES.DISABLE_REQUIRED_ACTIONS.PENDING_SESSION]: false,
    [PRIVILEGES.DISABLE_REQUIRED_ACTIONS.PENDING_SIGNATURES]: false,
  },
  SESSIONS: {
    [PRIVILEGES.SESSIONS.START_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_ACTIVITIES_NOTIFICATION]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_NOTES_NOTIFICATION]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_REPORT_REVIEW_NOTIFICATION]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_SESSION_NOTIFICATION]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_SIGNATURES_NOTIFICATION]: true,
  },
  NOTIFICATIONS: {
    [PRIVILEGES.NOTIFICATIONS.CANCELLED_APPOINTMENT]: false,
    [PRIVILEGES.NOTIFICATIONS.COMMENTS]: false,
    [PRIVILEGES.NOTIFICATIONS.FILE_UPLOAD_FAILED]: false,
    [PRIVILEGES.NOTIFICATIONS.MISSED_APPOINTMENT]: false,
    [PRIVILEGES.NOTIFICATIONS.PENDING_ACTIVITIES]: false,
    [PRIVILEGES.NOTIFICATIONS.PENDING_NOTES]: false,
    [PRIVILEGES.NOTIFICATIONS.PENDING_REPORT_REVIEW]: false,
    [PRIVILEGES.NOTIFICATIONS.PENDING_SESSION]: false,
    [PRIVILEGES.NOTIFICATIONS.PENDING_SIGNATURES]: false,
    [PRIVILEGES.NOTIFICATIONS.PROGRAM_MODIFICATION]: false,
    [PRIVILEGES.NOTIFICATIONS.RESTORED_APPOINTMENT]: false,
    [PRIVILEGES.NOTIFICATIONS.SESSION_FILES_ATTACHED]: false,
    [PRIVILEGES.NOTIFICATIONS.SESSION_FINISHED]: false,
    [PRIVILEGES.NOTIFICATIONS.SESSION_REMINDER]: false,
    [PRIVILEGES.NOTIFICATIONS.TARGET_MASTERY]: false,
    [PRIVILEGES.NOTIFICATIONS.TARGET_REOPENED]: false,
    [PRIVILEGES.NOTIFICATIONS.VIDEO_SESSION_INVITATION]: false,
  },
});

export const PRIVILEGES_BY_ROLE = /** @type {const} */ ({
  [userRoles.ADMINISTRATOR]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: true,
    [PRIVILEGES.AUDITOR.USE_OUTPUT]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FOLDERS]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FORMS]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FORMS]: true,
    [PRIVILEGES.LIBRARY.ACCESS_TEMPLATES_FOLDER]: true,
    [PRIVILEGES.LIBRARY.ADD_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.ADD_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.BULK_ADD_TAGS]: true,
    [PRIVILEGES.LIBRARY.BULK_DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.DELETE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.EDIT_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.MOVE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.MOVE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.RENAME_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.SHARE_FOLDERS]: true,
    [PRIVILEGES.OFFLINE.HAS_ACCESS]: true,
    [PRIVILEGES.PAGES.BILLING]: true,
    [PRIVILEGES.PAGES.CANCELLATION_REASONS]: true,
    [PRIVILEGES.PAGES.CLIENT_APPOINTMENTS]: false,
    [PRIVILEGES.PAGES.CLIENT_DATA]: true,
    [PRIVILEGES.PAGES.CLIENTS]: true,
    [PRIVILEGES.PAGES.FILES]: true,
    [PRIVILEGES.PAGES.FORMS]: true,
    [PRIVILEGES.PAGES.FUNDING_SOURCES]: true,
    [PRIVILEGES.PAGES.GLOBAL_AVAILABILITY]: true,
    [PRIVILEGES.PAGES.HISTORY]: true,
    [PRIVILEGES.PAGES.INTEGRATIONS]: true,
    [PRIVILEGES.PAGES.LIBRARY]: true,
    [PRIVILEGES.PAGES.MY_SCHEDULE]: false,
    [PRIVILEGES.PAGES.NOTES]: true,
    [PRIVILEGES.PAGES.PENDING_SIGNATURES]: true,
    [PRIVILEGES.PAGES.PLACE_OF_SERVICE]: true,
    [PRIVILEGES.PAGES.PROGRAM_DATA]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: false,
    [PRIVILEGES.PAGES.PROGRAM_TEMPLATES]: true,
    [PRIVILEGES.PAGES.PROGRAMS]: true,
    [PRIVILEGES.PAGES.PROGRESS]: true,
    [PRIVILEGES.PAGES.PROMPT_LEVELS]: true,
    [PRIVILEGES.PAGES.PROTOTYPE]: true,
    [PRIVILEGES.PAGES.REPORTING]: true,
    [PRIVILEGES.PAGES.REPORTS]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: true,
    [PRIVILEGES.PAGES.STAFF_INFORMATION]: true,
    [PRIVILEGES.PAGES.SUPERVISION_MODE]: true,
    [PRIVILEGES.PAGES.TAGS]: true,
    [PRIVILEGES.PAGES.TREATMENT_AREAS]: true,
    [PRIVILEGES.PAGES.TREATMENT_SERVICES]: true,
    [PRIVILEGES.PAGES.WORKFLOWS]: true,
    [PRIVILEGES.PROGRAMS.ADD_TARGET]: true,
    [PRIVILEGES.PROGRAMS.CHANGE_PROGRAM_TYPE]: true,
    [PRIVILEGES.PROGRAMS.CUSTOMIZE_PROMPT_LEVELS]: true,
    [PRIVILEGES.PROGRAMS.EDIT_TARGET_STATUS]: true,
    [PRIVILEGES.PROGRAMS.UPSERT_PROGRAMS_WITH_MIXED_MEASUREMENT_TYPES]: true,
    [PRIVILEGES.REPORTS.ADD_ORG_INSIGHTS_GRAPH]: false,
    [PRIVILEGES.REPORTS.COMMENT_REPORTS]: true,
    [PRIVILEGES.REPORTS.DELETE_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.DELETE_REPORTS]: true,
    [PRIVILEGES.REPORTS.EDIT_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.EDIT_REPORTS]: true,
    [PRIVILEGES.REPORTS.VIEW_ARCHIVED]: true,
    [PRIVILEGES.REPORTS.VIEW_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.VIEW_DRAFT]: true,
    [PRIVILEGES.REPORTS.VIEW_IN_REVIEW]: true,
    [PRIVILEGES.REPORTS.VIEW_OVERVIEW]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_APPROVE_CHANGE_REQUESTS]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_OVERLAPPING_ACTIVITIES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_SIGN_AND_SEND]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_VOICE_INPUT]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.CANCEL_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.COMPLETE_OTHER_USERS_NOTES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_ASSOCIATE_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_OWN_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.PRESERVE_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.REOPEN]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.UNRENDER]: true,
    [PRIVILEGES.SESSIONS.ADD_COMMENT]: true,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: true,
    [PRIVILEGES.SESSIONS.CHANGE_SESSION_NOTE_TEMPLATE]: true,
    [PRIVILEGES.SESSIONS.DELETE]: true,
    [PRIVILEGES.SESSIONS.DELETE_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.EDIT_DATA]: true,
    [PRIVILEGES.SESSIONS.EDIT_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.ENTER_MANUAL_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.LEAVE_OPEN]: true,
    [PRIVILEGES.SESSIONS.SIGN_OTHER_SIGNATURE_REQUESTS]: true,
    [PRIVILEGES.SESSIONS.START_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.SUPERVISE_SESSIONS]: true,
    [PRIVILEGES.USERS.ADD_STAFF_PROFILES]: true,
    [PRIVILEGES.USERS.MANAGE_USERS]: true,
    ...DEFAULT_PRIVILEGES.DISABLE_REQUIRED_ACTIONS,
    ...DEFAULT_PRIVILEGES.SESSIONS,
    ...DEFAULT_PRIVILEGES.NOTIFICATIONS,
  },
  [userRoles.MANAGER]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: true,
    [PRIVILEGES.AUDITOR.USE_OUTPUT]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FOLDERS]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FORMS]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FORMS]: true,
    [PRIVILEGES.LIBRARY.ADD_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.ADD_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.BULK_ADD_TAGS]: true,
    [PRIVILEGES.LIBRARY.BULK_DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.DELETE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.EDIT_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.MOVE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.MOVE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.RENAME_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.SHARE_FOLDERS]: true,
    [PRIVILEGES.OFFLINE.HAS_ACCESS]: true,
    [PRIVILEGES.PAGES.BILLING]: true,
    [PRIVILEGES.PAGES.CANCELLATION_REASONS]: true,
    [PRIVILEGES.PAGES.CLIENT_DATA]: true,
    [PRIVILEGES.PAGES.CLIENTS]: true,
    [PRIVILEGES.PAGES.FILES]: true,
    [PRIVILEGES.PAGES.FORMS]: true,
    [PRIVILEGES.PAGES.FUNDING_SOURCES]: true,
    [PRIVILEGES.PAGES.GLOBAL_AVAILABILITY]: true,
    [PRIVILEGES.PAGES.HISTORY]: true,
    [PRIVILEGES.PAGES.INTEGRATIONS]: true,
    [PRIVILEGES.PAGES.LIBRARY]: true,
    [PRIVILEGES.PAGES.NOTES]: true,
    [PRIVILEGES.PAGES.PENDING_SIGNATURES]: true,
    [PRIVILEGES.PAGES.PLACE_OF_SERVICE]: true,
    [PRIVILEGES.PAGES.PROGRAM_DATA]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: true,
    [PRIVILEGES.PAGES.PROGRAM_TEMPLATES]: true,
    [PRIVILEGES.PAGES.PROGRAMS]: true,
    [PRIVILEGES.PAGES.PROGRESS]: true,
    [PRIVILEGES.PAGES.PROMPT_LEVELS]: true,
    [PRIVILEGES.PAGES.PROTOTYPE]: true,
    [PRIVILEGES.PAGES.REPORTING]: true,
    [PRIVILEGES.PAGES.REPORTS]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: true,
    [PRIVILEGES.PAGES.STAFF_INFORMATION]: true,
    [PRIVILEGES.PAGES.SUPERVISION_MODE]: true,
    [PRIVILEGES.PAGES.TAGS]: true,
    [PRIVILEGES.PAGES.TREATMENT_AREAS]: true,
    [PRIVILEGES.PAGES.TREATMENT_SERVICES]: true,
    [PRIVILEGES.PAGES.WORKFLOWS]: true,
    [PRIVILEGES.PROGRAMS.ADD_PROGRAMS_TO_LIBRARY]: true,
    [PRIVILEGES.PROGRAMS.ADD_TARGET]: true,
    [PRIVILEGES.PROGRAMS.CHANGE_PROGRAM_TYPE]: true,
    [PRIVILEGES.PROGRAMS.CUSTOMIZE_PROMPT_LEVELS]: true,
    [PRIVILEGES.PROGRAMS.EDIT_TARGET_STATUS]: true,
    [PRIVILEGES.PROGRAMS.UPSERT_PROGRAMS_WITH_MIXED_MEASUREMENT_TYPES]: true,
    [PRIVILEGES.REPORTS.ADD_ORG_INSIGHTS_GRAPH]: true,
    [PRIVILEGES.REPORTS.COMMENT_REPORTS]: true,
    [PRIVILEGES.REPORTS.DELETE_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.DELETE_REPORTS]: true,
    [PRIVILEGES.REPORTS.EDIT_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.EDIT_REPORTS]: true,
    [PRIVILEGES.REPORTS.VIEW_ARCHIVED]: true,
    [PRIVILEGES.REPORTS.VIEW_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.VIEW_DRAFT]: true,
    [PRIVILEGES.REPORTS.VIEW_IN_REVIEW]: true,
    [PRIVILEGES.REPORTS.VIEW_OVERVIEW]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_APPROVE_CHANGE_REQUESTS]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_OVERLAPPING_ACTIVITIES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_SIGN_AND_SEND]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_VOICE_INPUT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.CANCEL_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.COMPLETE_OTHER_USERS_NOTES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_ASSOCIATE_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_OWN_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.PRESERVE_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.REOPEN]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.UNRENDER]: true,
    [PRIVILEGES.SESSIONS.ADD_COMMENT]: true,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: true,
    [PRIVILEGES.SESSIONS.CHANGE_SESSION_NOTE_TEMPLATE]: true,
    [PRIVILEGES.SESSIONS.DELETE]: true,
    [PRIVILEGES.SESSIONS.DELETE_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.EDIT_DATA]: true,
    [PRIVILEGES.SESSIONS.EDIT_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.ENTER_MANUAL_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.LEAVE_OPEN]: true,
    [PRIVILEGES.SESSIONS.SIGN_OTHER_SIGNATURE_REQUESTS]: true,
    [PRIVILEGES.SESSIONS.SUPERVISE_SESSIONS]: true,
    [PRIVILEGES.USERS.ADD_STAFF_PROFILES]: true,
    [PRIVILEGES.USERS.MANAGE_USERS]: true,
    ...DEFAULT_PRIVILEGES.DISABLE_REQUIRED_ACTIONS,
    ...DEFAULT_PRIVILEGES.SESSIONS,
    ...DEFAULT_PRIVILEGES.NOTIFICATIONS,
  },
  [userRoles.SUPERVISOR]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: false,
    [PRIVILEGES.AUDITOR.USE_OUTPUT]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: false,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: false,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: false,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FOLDERS]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FORMS]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FORMS]: true,
    [PRIVILEGES.LIBRARY.ADD_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.ADD_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.BULK_ADD_TAGS]: false,
    [PRIVILEGES.LIBRARY.DELETE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.EDIT_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.MOVE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.MOVE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.RENAME_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.SHARE_FOLDERS]: false,
    [PRIVILEGES.OFFLINE.HAS_ACCESS]: true,
    [PRIVILEGES.PAGES.FILES]: true,
    [PRIVILEGES.PAGES.HISTORY]: true,
    [PRIVILEGES.PAGES.LIBRARY]: true,
    [PRIVILEGES.PAGES.PENDING_SIGNATURES]: true,
    [PRIVILEGES.PAGES.PROGRAM_DATA]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: false,
    [PRIVILEGES.PAGES.PROGRAM_TEMPLATES]: true,
    [PRIVILEGES.PAGES.PROGRAMS]: true,
    [PRIVILEGES.PAGES.PROGRESS]: true,
    [PRIVILEGES.PAGES.PROMPT_LEVELS]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: false,
    [PRIVILEGES.PAGES.TAGS]: true,
    [PRIVILEGES.PAGES.WORKFLOWS]: true,
    [PRIVILEGES.PROGRAMS.ADD_PROGRAMS_TO_LIBRARY]: true,
    [PRIVILEGES.PROGRAMS.ADD_TARGET]: true,
    [PRIVILEGES.PROGRAMS.CHANGE_PROGRAM_TYPE]: false,
    [PRIVILEGES.PROGRAMS.CUSTOMIZE_PROMPT_LEVELS]: false,
    [PRIVILEGES.PROGRAMS.EDIT_TARGET_STATUS]: true,
    [PRIVILEGES.PROGRAMS.UPSERT_PROGRAMS_WITH_MIXED_MEASUREMENT_TYPES]: true,
    [PRIVILEGES.REPORTS.ADD_ORG_INSIGHTS_GRAPH]: false,
    [PRIVILEGES.REPORTS.COMMENT_REPORTS]: true,
    [PRIVILEGES.REPORTS.DELETE_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.DELETE_REPORTS]: true,
    [PRIVILEGES.REPORTS.EDIT_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.EDIT_REPORTS]: true,
    [PRIVILEGES.REPORTS.VIEW_ARCHIVED]: true,
    [PRIVILEGES.REPORTS.VIEW_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.VIEW_DRAFT]: true,
    [PRIVILEGES.REPORTS.VIEW_IN_REVIEW]: true,
    [PRIVILEGES.REPORTS.VIEW_OVERVIEW]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_OVERLAPPING_ACTIVITIES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_SIGN_AND_SEND]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_VOICE_INPUT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.CANCEL_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_OWN_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.PRESERVE_SIGNATURES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.UNRENDER]: false,
    [PRIVILEGES.SESSIONS.ADD_COMMENT]: true,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: false,
    [PRIVILEGES.SESSIONS.CHANGE_SESSION_NOTE_TEMPLATE]: true,
    [PRIVILEGES.SESSIONS.DELETE_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.EDIT_DATA]: true,
    [PRIVILEGES.SESSIONS.EDIT_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.ENTER_MANUAL_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.LEAVE_OPEN]: true,
    [PRIVILEGES.SESSIONS.SIGN_OTHER_SIGNATURE_REQUESTS]: true,
    [PRIVILEGES.SESSIONS.SUPERVISE_SESSIONS]: true,
    ...DEFAULT_PRIVILEGES.DISABLE_REQUIRED_ACTIONS,
    ...DEFAULT_PRIVILEGES.SESSIONS,
    ...DEFAULT_PRIVILEGES.NOTIFICATIONS,
  },
  [userRoles.SENIOR_THERAPIST]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: false,
    [PRIVILEGES.AUDITOR.USE_OUTPUT]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: false,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: false,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: false,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FOLDERS]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FORMS]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FORMS]: true,
    [PRIVILEGES.LIBRARY.ADD_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.ADD_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.EDIT_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.MOVE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.MOVE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.RENAME_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.SHARE_FOLDERS]: false,
    [PRIVILEGES.OFFLINE.HAS_ACCESS]: true,
    [PRIVILEGES.PAGES.FILES]: true,
    [PRIVILEGES.PAGES.HISTORY]: true,
    [PRIVILEGES.PAGES.LIBRARY]: true,
    [PRIVILEGES.PAGES.PENDING_SIGNATURES]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: false,
    [PRIVILEGES.PAGES.PROGRAMS]: true,
    [PRIVILEGES.PAGES.PROGRESS]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: false,
    [PRIVILEGES.PROGRAMS.ADD_PROGRAMS_TO_LIBRARY]: true,
    [PRIVILEGES.PROGRAMS.ADD_TARGET]: true,
    [PRIVILEGES.PROGRAMS.CHANGE_PROGRAM_TYPE]: false,
    [PRIVILEGES.PROGRAMS.CUSTOMIZE_PROMPT_LEVELS]: false,
    [PRIVILEGES.PROGRAMS.EDIT_TARGET_STATUS]: true,
    [PRIVILEGES.PROGRAMS.UPSERT_PROGRAMS_WITH_MIXED_MEASUREMENT_TYPES]: true,
    [PRIVILEGES.REPORTS.ADD_ORG_INSIGHTS_GRAPH]: false,
    [PRIVILEGES.REPORTS.COMMENT_REPORTS]: true,
    [PRIVILEGES.REPORTS.DELETE_ASSESSMENTS]: false,
    [PRIVILEGES.REPORTS.EDIT_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.EDIT_REPORTS]: true,
    [PRIVILEGES.REPORTS.VIEW_ARCHIVED]: true,
    [PRIVILEGES.REPORTS.VIEW_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.VIEW_DRAFT]: true,
    [PRIVILEGES.REPORTS.VIEW_IN_REVIEW]: true,
    [PRIVILEGES.REPORTS.VIEW_OVERVIEW]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_OVERLAPPING_ACTIVITIES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_SIGN_AND_SEND]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_VOICE_INPUT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.CANCEL_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_OWN_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.PRESERVE_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.UNRENDER]: false,
    [PRIVILEGES.SESSIONS.ADD_COMMENT]: true,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: false,
    [PRIVILEGES.SESSIONS.CHANGE_SESSION_NOTE_TEMPLATE]: true,
    [PRIVILEGES.SESSIONS.DELETE_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.EDIT_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.ENTER_MANUAL_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.LEAVE_OPEN]: true,
    [PRIVILEGES.SESSIONS.SIGN_OTHER_SIGNATURE_REQUESTS]: true,
    [PRIVILEGES.SESSIONS.SUPERVISE_SESSIONS]: true,
    ...DEFAULT_PRIVILEGES.DISABLE_REQUIRED_ACTIONS,
    ...DEFAULT_PRIVILEGES.SESSIONS,
    ...DEFAULT_PRIVILEGES.NOTIFICATIONS,
  },
  [userRoles.TRAINER]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: false,
    [PRIVILEGES.AUDITOR.USE_OUTPUT]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: false,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: false,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: false,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FILES]: false,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FOLDERS]: false,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FORMS]: false,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FILES]: false,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FORMS]: false,
    [PRIVILEGES.OFFLINE.HAS_ACCESS]: true,
    [PRIVILEGES.PAGES.FILES]: true,
    [PRIVILEGES.PAGES.HISTORY]: true,
    [PRIVILEGES.PAGES.LIBRARY]: false,
    [PRIVILEGES.PAGES.PENDING_SIGNATURES]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: false,
    [PRIVILEGES.PAGES.PROGRAMS]: false,
    [PRIVILEGES.PAGES.PROGRESS]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: false,
    [PRIVILEGES.PROGRAMS.CHANGE_PROGRAM_TYPE]: false,
    [PRIVILEGES.PROGRAMS.CUSTOMIZE_PROMPT_LEVELS]: false,
    [PRIVILEGES.REPORTS.ADD_ORG_INSIGHTS_GRAPH]: false,
    [PRIVILEGES.REPORTS.VIEW_OVERVIEW]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_OVERLAPPING_ACTIVITIES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_SIGN_AND_SEND]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_VOICE_INPUT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.CANCEL_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_OWN_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.PRESERVE_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.REOPEN]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.UNRENDER]: false,
    [PRIVILEGES.SESSIONS.ADD_COMMENT]: true,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: false,
    [PRIVILEGES.SESSIONS.CHANGE_SESSION_NOTE_TEMPLATE]: true,
    [PRIVILEGES.SESSIONS.DELETE_SCHEDULED_SESSIONS]: false,
    [PRIVILEGES.SESSIONS.EDIT_SCHEDULED_SESSIONS]: false,
    [PRIVILEGES.SESSIONS.ENTER_MANUAL_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.LEAVE_OPEN]: true,
    [PRIVILEGES.SESSIONS.SIGN_OTHER_SIGNATURE_REQUESTS]: true,
    [PRIVILEGES.SESSIONS.SUPERVISE_SESSIONS]: false,
    ...DEFAULT_PRIVILEGES.DISABLE_REQUIRED_ACTIONS,
    ...DEFAULT_PRIVILEGES.SESSIONS,
    ...DEFAULT_PRIVILEGES.NOTIFICATIONS,
  },
  [userRoles.PARENT]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: false,
    [PRIVILEGES.AUDITOR.USE_OUTPUT]: false,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: false,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: false,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: false,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FILES]: false,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FOLDERS]: false,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FORMS]: false,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FILES]: false,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FORMS]: false,
    [PRIVILEGES.HOME.SHOW_ALL_CLIENT_PROGRAMS]: false,
    [PRIVILEGES.OFFLINE.HAS_ACCESS]: true,
    [PRIVILEGES.PAGES.FILES]: true,
    [PRIVILEGES.PAGES.HISTORY]: true,
    [PRIVILEGES.PAGES.LIBRARY]: false,
    [PRIVILEGES.PAGES.PENDING_SIGNATURES]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: false,
    [PRIVILEGES.PAGES.PROGRAMS]: false,
    [PRIVILEGES.PAGES.PROGRESS]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: false,
    [PRIVILEGES.PROGRAMS.ADD_TARGET]: false,
    [PRIVILEGES.PROGRAMS.CHANGE_PROGRAM_TYPE]: false,
    [PRIVILEGES.PROGRAMS.CUSTOMIZE_PROMPT_LEVELS]: false,
    [PRIVILEGES.PROGRAMS.EDIT_TARGET_STATUS]: false,
    [PRIVILEGES.REPORTS.ADD_ORG_INSIGHTS_GRAPH]: false,
    [PRIVILEGES.REPORTS.VIEW_OVERVIEW]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_OVERLAPPING_ACTIVITIES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_SIGN_AND_SEND]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_VOICE_INPUT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.CANCEL_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_OWN_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.PRESERVE_SIGNATURES]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.REOPEN]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENT]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.UNRENDER]: false,
    [PRIVILEGES.SESSIONS.ADD_COMMENT]: false,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: false,
    [PRIVILEGES.SESSIONS.CHANGE_SESSION_NOTE_TEMPLATE]: false,
    [PRIVILEGES.SESSIONS.DELETE_SCHEDULED_SESSIONS]: false,
    [PRIVILEGES.SESSIONS.EDIT_SCHEDULED_SESSIONS]: false,
    [PRIVILEGES.SESSIONS.ENTER_MANUAL_SESSIONS]: false,
    [PRIVILEGES.SESSIONS.LEAVE_OPEN]: false,
    [PRIVILEGES.SESSIONS.SUPERVISE_SESSIONS]: false,
    ...DEFAULT_PRIVILEGES.DISABLE_REQUIRED_ACTIONS,
    ...DEFAULT_PRIVILEGES.SESSIONS,
    ...DEFAULT_PRIVILEGES.NOTIFICATIONS,
  },
});

export const PRIVILEGES_LOCKED_BY_ROLE = /** @type {const} */ ({
  [userRoles.ADMINISTRATOR]: {
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: true,
    [PRIVILEGES.USERS.MANAGE_USERS]: true,
  },
  [userRoles.MANAGER]: {
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FOLDERS]: true,
    [PRIVILEGES.FILES_AND_FORMS.MANAGE_FORMS]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FILES]: true,
    [PRIVILEGES.FILES_AND_FORMS.DELETE_FORMS]: true,
    [PRIVILEGES.LIBRARY.ADD_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.ADD_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.DELETE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.EDIT_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.RENAME_FOLDERS]: true,
    [PRIVILEGES.PAGES.BILLING]: true,
    [PRIVILEGES.PAGES.CANCELLATION_REASONS]: true,
    [PRIVILEGES.PAGES.CLIENT_DATA]: true,
    [PRIVILEGES.PAGES.CLIENTS]: true,
    [PRIVILEGES.PAGES.FORMS]: true,
    [PRIVILEGES.PAGES.FUNDING_SOURCES]: true,
    [PRIVILEGES.PAGES.GLOBAL_AVAILABILITY]: true,
    [PRIVILEGES.PAGES.INTEGRATIONS]: true,
    [PRIVILEGES.PAGES.LIBRARY]: true,
    [PRIVILEGES.PAGES.NOTES]: true,
    [PRIVILEGES.PAGES.PLACE_OF_SERVICE]: true,
    [PRIVILEGES.PAGES.PROGRAM_DATA]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: true,
    [PRIVILEGES.PAGES.PROGRAM_TEMPLATES]: true,
    [PRIVILEGES.PAGES.PROMPT_LEVELS]: true,
    [PRIVILEGES.PAGES.PROTOTYPE]: true,
    [PRIVILEGES.PAGES.REPORTING]: true,
    [PRIVILEGES.PAGES.REPORTS]: true,
    [PRIVILEGES.PAGES.STAFF_INFORMATION]: true,
    [PRIVILEGES.PAGES.SUPERVISION_MODE]: true,
    [PRIVILEGES.PAGES.TAGS]: true,
    [PRIVILEGES.PAGES.TREATMENT_AREAS]: true,
    [PRIVILEGES.PAGES.TREATMENT_SERVICES]: true,
    [PRIVILEGES.PAGES.WORKFLOWS]: true,
    [PRIVILEGES.PROGRAMS.ADD_TARGET]: true,
    [PRIVILEGES.PROGRAMS.EDIT_TARGET_STATUS]: true,
    [PRIVILEGES.REPORTS.VIEW_DRAFT]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.COMPLETE_OTHER_USERS_NOTES]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_ASSOCIATE_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.EDIT_OWN_ACTIVITY_TIME_FRAME]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.REOPEN]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_REPORT_REVIEW_NOTIFICATION]: true,
    [PRIVILEGES.USERS.MANAGE_USERS]: true,
  },
  [userRoles.SUPERVISOR]: {
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: false,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: false,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_REPORT_REVIEW_NOTIFICATION]: true,
  },
  [userRoles.SENIOR_THERAPIST]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: true,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: true,
    [PRIVILEGES.PAGES.GLOBAL_AVAILABILITY]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: false,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_APPROVE_CHANGE_REQUESTS]: true,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_REPORT_REVIEW_NOTIFICATION]: true,
    [PRIVILEGES.USERS.ADD_STAFF_PROFILES]: true,
    [PRIVILEGES.USERS.MANAGE_USERS]: true,
  },
  [userRoles.TRAINER]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: true,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: true,
    [PRIVILEGES.LIBRARY.ADD_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.ADD_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.BULK_ADD_TAGS]: true,
    [PRIVILEGES.LIBRARY.BULK_DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.DELETE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.EDIT_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.MOVE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.MOVE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.RENAME_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.SHARE_FOLDERS]: true,
    [PRIVILEGES.PAGES.BILLING]: true,
    [PRIVILEGES.PAGES.CANCELLATION_REASONS]: true,
    [PRIVILEGES.PAGES.CLIENT_DATA]: true,
    [PRIVILEGES.PAGES.CLIENTS]: true,
    [PRIVILEGES.PAGES.FORMS]: true,
    [PRIVILEGES.PAGES.FUNDING_SOURCES]: true,
    [PRIVILEGES.PAGES.GLOBAL_AVAILABILITY]: true,
    [PRIVILEGES.PAGES.INTEGRATIONS]: true,
    [PRIVILEGES.PAGES.LIBRARY]: true,
    [PRIVILEGES.PAGES.NOTES]: true,
    [PRIVILEGES.PAGES.PLACE_OF_SERVICE]: true,
    [PRIVILEGES.PAGES.PROGRAM_DATA]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: true,
    [PRIVILEGES.PAGES.PROGRAM_TEMPLATES]: true,
    [PRIVILEGES.PAGES.PROGRAMS]: true,
    [PRIVILEGES.PAGES.PROMPT_LEVELS]: true,
    [PRIVILEGES.PAGES.PROTOTYPE]: true,
    [PRIVILEGES.PAGES.REPORTING]: true,
    [PRIVILEGES.PAGES.REPORTS]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: true,
    [PRIVILEGES.PAGES.STAFF_INFORMATION]: true,
    [PRIVILEGES.PAGES.SUPERVISION_MODE]: true,
    [PRIVILEGES.PAGES.TAGS]: true,
    [PRIVILEGES.PAGES.TREATMENT_AREAS]: true,
    [PRIVILEGES.PAGES.TREATMENT_SERVICES]: true,
    [PRIVILEGES.PAGES.WORKFLOWS]: true,
    [PRIVILEGES.PROGRAMS.CHANGE_PROGRAM_TYPE]: true,
    [PRIVILEGES.PROGRAMS.CUSTOMIZE_PROMPT_LEVELS]: true,
    [PRIVILEGES.PROGRAMS.UPSERT_PROGRAMS_WITH_MIXED_MEASUREMENT_TYPES]: true,
    [PRIVILEGES.REPORTS.COMMENT_REPORTS]: true,
    [PRIVILEGES.REPORTS.DELETE_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.DELETE_REPORTS]: true,
    [PRIVILEGES.REPORTS.EDIT_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.EDIT_REPORTS]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_APPROVE_CHANGE_REQUESTS]: true,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: true,
    [PRIVILEGES.SESSIONS.DELETE_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.EDIT_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_REPORT_REVIEW_NOTIFICATION]: true,
    [PRIVILEGES.SESSIONS.SUPERVISE_SESSIONS]: true,
    [PRIVILEGES.USERS.ADD_STAFF_PROFILES]: true,
    [PRIVILEGES.USERS.MANAGE_USERS]: true,
  },
  [userRoles.PARENT]: {
    [PRIVILEGES.AUDITOR.MANAGE_BEHAVIOR]: true,
    [PRIVILEGES.AUDITOR.USE_OUTPUT]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_CLIENTS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ACCESS_ALL_STAFF_MEMBERS_IN_OWN_UNITS]: true,
    [PRIVILEGES.CLIENTS.ADD_CLIENTS]: true,
    [PRIVILEGES.LIBRARY.ADD_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.ADD_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.BULK_ADD_TAGS]: true,
    [PRIVILEGES.LIBRARY.BULK_DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.DELETE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.DELETE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.EDIT_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.MOVE_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.MOVE_PROGRAMS]: true,
    [PRIVILEGES.LIBRARY.RENAME_FOLDERS]: true,
    [PRIVILEGES.LIBRARY.SHARE_FOLDERS]: true,
    [PRIVILEGES.PAGES.BILLING]: true,
    [PRIVILEGES.PAGES.CANCELLATION_REASONS]: true,
    [PRIVILEGES.PAGES.CLIENT_DATA]: true,
    [PRIVILEGES.PAGES.CLIENTS]: true,
    [PRIVILEGES.PAGES.FORMS]: true,
    [PRIVILEGES.PAGES.FUNDING_SOURCES]: true,
    [PRIVILEGES.PAGES.GLOBAL_AVAILABILITY]: true,
    [PRIVILEGES.PAGES.INTEGRATIONS]: true,
    [PRIVILEGES.PAGES.LIBRARY]: true,
    [PRIVILEGES.PAGES.NOTES]: true,
    [PRIVILEGES.PAGES.PLACE_OF_SERVICE]: true,
    [PRIVILEGES.PAGES.PROGRAM_DATA]: true,
    [PRIVILEGES.PAGES.PROGRAM_LOGIC]: true,
    [PRIVILEGES.PAGES.PROGRAM_TEMPLATES]: true,
    [PRIVILEGES.PAGES.PROGRAMS]: true,
    [PRIVILEGES.PAGES.PROMPT_LEVELS]: true,
    [PRIVILEGES.PAGES.PROTOTYPE]: true,
    [PRIVILEGES.PAGES.REPORTING]: true,
    [PRIVILEGES.PAGES.REPORTS]: true,
    [PRIVILEGES.PAGES.SCHEDULING]: true,
    [PRIVILEGES.PAGES.STAFF_INFORMATION]: true,
    [PRIVILEGES.PAGES.SUPERVISION_MODE]: true,
    [PRIVILEGES.PAGES.TAGS]: true,
    [PRIVILEGES.PAGES.TREATMENT_AREAS]: true,
    [PRIVILEGES.PAGES.TREATMENT_SERVICES]: true,
    [PRIVILEGES.PAGES.WORKFLOWS]: true,
    [PRIVILEGES.PROGRAMS.CHANGE_PROGRAM_TYPE]: true,
    [PRIVILEGES.PROGRAMS.CUSTOMIZE_PROMPT_LEVELS]: true,
    [PRIVILEGES.PROGRAMS.UPSERT_PROGRAMS_WITH_MIXED_MEASUREMENT_TYPES]: true,
    [PRIVILEGES.REPORTS.ADD_ORG_INSIGHTS_GRAPH]: true,
    [PRIVILEGES.REPORTS.COMMENT_REPORTS]: true,
    [PRIVILEGES.REPORTS.DELETE_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.DELETE_REPORTS]: true,
    [PRIVILEGES.REPORTS.EDIT_ASSESSMENTS]: true,
    [PRIVILEGES.REPORTS.EDIT_REPORTS]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.ALLOW_APPROVE_CHANGE_REQUESTS]: true,
    [PRIVILEGES.SESSION_ACTIVITIES.DELETE]: true,
    [PRIVILEGES.SESSIONS.AI_AUDIT_AGENT]: true,
    [PRIVILEGES.SESSIONS.DELETE]: true,
    [PRIVILEGES.SESSIONS.DELETE_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.EDIT_DATA]: true,
    [PRIVILEGES.SESSIONS.EDIT_SCHEDULED_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.ENTER_MANUAL_SESSIONS]: true,
    [PRIVILEGES.SESSIONS.START_WITH_PENDING_REPORT_REVIEW_NOTIFICATION]: true,
    [PRIVILEGES.SESSIONS.SUPERVISE_SESSIONS]: true,
    [PRIVILEGES.USERS.ADD_STAFF_PROFILES]: true,
    [PRIVILEGES.USERS.MANAGE_USERS]: true,
  },
});

/**
 * Billing items, the billing worklist and every billing task, for every client in the organization:
 * billing is an org function rather than a caseload one. The backend mirrors it in
 * `functions/utils/billingPrivileges.js`, and `privileges.test.js` fails if the two drift.
 */
export const BILLING_PRIVILEGE = PRIVILEGES.PAGES.BILLING;
